import type { FC } from "react"
import { ZoomIn, ZoomOut } from "lucide-react"

import type { OhlcWindow } from "@/domains/thick-v1/stores/pool-ohlc-store"
import type { ManualClPoolOhlcPoint } from "@/domains/validators"
import { cn } from "@/domains/library"

/**
 * Compact price-history chart sized to live inside the create-position card,
 * directly above the tick-based liquidity brush. The two charts cannot share
 * an x-axis — one is time, the other is tick-space — so we keep them stacked
 * but colour-matched, and overlay the user's currently-selected price range
 * as a horizontal band on top of the historical close line.
 *
 * The band is the unique UX affordance here: at a glance a liquidity provider
 * can see whether the price they're about to centre their range on would
 * have stayed in-range over the past week / month. That feedback loop is
 * exactly what the pool-detail page's richer `PoolOhlcChartView` does not
 * give them at the moment of range selection.
 *
 * Rendering is pure SVG so the chart renders in Storybook and unit tests
 * without mounting echarts. The view is entirely prop-driven — the composite
 * next to this file owns all store coupling.
 */
export interface PriceHistoryWithRangeViewProps {
  /** Points sorted oldest-first. Empty array renders the empty state. */
  points: ReadonlyArray<ManualClPoolOhlcPoint>
  /** Currently-selected lookback window; drives the tab selection only. */
  window: OhlcWindow
  /** User's selected min price (canonical pool orientation). */
  minPrice: number | undefined
  /** User's selected max price (canonical pool orientation). */
  maxPrice: number | undefined
  /** Pool's current price; drawn as a dashed horizontal reference line. */
  currentPrice: number | undefined
  /** Whether the proposed range currently contains the pool's current price. */
  isInRange: boolean
  /** Loading state — renders a subtle skeleton row over the chart. */
  isLoading: boolean
  /** User wants to swap the lookback; store updates and refetches. */
  onWindowChange: (next: OhlcWindow) => void
  /** Token symbols for the header label. */
  baseSymbol: string
  quoteSymbol: string
  className?: string
  /** Rendering height in pixels. Kept compact so it nests inside the card. */
  height?: number
  /**
   * Multiplier applied to the derived y-axis span (centered on the close
   * median). `1` is the natural fit-to-data view; values `< 1` zoom in
   * (tighter price detail) and `> 1` zoom out (headroom to include the
   * user's selected min/max band even when it sits outside the close
   * envelope). Defaults to `1`.
   */
  zoomFactor?: number
  /** Fired when the user clicks the zoom-in affordance. */
  onZoomIn?: () => void
  /** Fired when the user clicks the zoom-out affordance. */
  onZoomOut?: () => void
  /** Whether the zoom-in button should read as enabled. Defaults to true. */
  canZoomIn?: boolean
  /** Whether the zoom-out button should read as enabled. Defaults to true. */
  canZoomOut?: boolean
}

const WINDOW_OPTIONS: Array<{ id: OhlcWindow; label: string }> = [
  { id: "24h", label: "24H" },
  { id: "7d", label: "7D" },
  { id: "30d", label: "30D" },
]

const CHART_WIDTH = 640
const DEFAULT_HEIGHT = 120
const CHART_PAD_X = 4
const CHART_PAD_Y = 10

export const PriceHistoryWithRangeView: FC<PriceHistoryWithRangeViewProps> = ({
  points,
  window,
  minPrice,
  maxPrice,
  currentPrice,
  isInRange,
  isLoading,
  onWindowChange,
  baseSymbol,
  quoteSymbol,
  className,
  height = DEFAULT_HEIGHT,
  zoomFactor = 1,
  onZoomIn,
  onZoomOut,
  canZoomIn = true,
  canZoomOut = true,
}) => {
  const closeValues = points
    .map((point) => point.close ?? point.open)
    .filter((value): value is number => value !== undefined && Number.isFinite(value))

  // The y-scale domain is derived from the historical close values alone.
  // Earlier iterations folded `currentPrice`, `minPrice` and `maxPrice` in —
  // but these come from `tickToPriceString` (token1 / token0) while the
  // indexer's `close` field is stored in the inverse orientation for some
  // pools (token0 / token1). Folding a 4000-scale number in with a 0.00025
  // close squashed the price line into a flat ribbon at the chart edge.
  // Scaling off closes alone matches what the pool-detail chart does and is
  // what makes the line visible.
  const rawMin = closeValues.length ? Math.min(...closeValues) : 0
  const rawMax = closeValues.length ? Math.max(...closeValues) : 1
  const rawSpan = rawMax - rawMin || Math.max(Math.abs(rawMax), 1) * 0.01
  // Natural (zoom=1) view pads 8% each side. Zooming multiplies the span
  // around the close median, so zooming out can pull the user's selected
  // min/max band into view even when it sits well outside the raw close
  // envelope.
  const safeZoom = Math.max(0.05, Number.isFinite(zoomFactor) ? zoomFactor : 1)
  const center = (rawMin + rawMax) / 2
  const halfSpan = (rawSpan * 0.5 + rawSpan * 0.08) * safeZoom
  const minValue = center - halfSpan
  const maxValue = center + halfSpan
  const range = maxValue - minValue || 1

  const usableH = height - CHART_PAD_Y * 2
  const priceToY = (price: number): number => {
    const clamped = Math.max(minValue, Math.min(maxValue, price))
    const ratio = (clamped - minValue) / range
    return CHART_PAD_Y + usableH - ratio * usableH
  }

  const step = closeValues.length > 1 ? (CHART_WIDTH - CHART_PAD_X * 2) / (closeValues.length - 1) : 0

  const pricePath = closeValues
    .map((value, index) => {
      const x = CHART_PAD_X + index * step
      const y = priceToY(value)
      return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(" ")

  // Reference lines / band only render when the value actually falls inside
  // the close-value envelope. Clamping them to the chart edge would lie —
  // either the orientation is inverted (so the number is meaningless) or the
  // selection genuinely sits outside the window, in which case a glued-to-
  // edge line looks exactly like one that happens to be at the chart top.
  // Out-of-frame bounds surface as a single `price-history-range-status`
  // badge in the header instead.
  const minInFrame =
    minPrice !== undefined && Number.isFinite(minPrice) && minPrice >= minValue && minPrice <= maxValue
  const maxInFrame =
    maxPrice !== undefined && Number.isFinite(maxPrice) && maxPrice >= minValue && maxPrice <= maxValue
  const currentInFrame =
    currentPrice !== undefined &&
    Number.isFinite(currentPrice) &&
    currentPrice >= minValue &&
    currentPrice <= maxValue

  const minY = minInFrame ? priceToY(minPrice!) : undefined
  const maxY = maxInFrame ? priceToY(maxPrice!) : undefined
  const currentY = currentInFrame ? priceToY(currentPrice!) : undefined

  // `minY` corresponds to the LOWER price, which maps to a HIGHER y on the
  // chart (SVG y grows downward). Guard the band against inverted input.
  const bandTop = minY !== undefined && maxY !== undefined ? Math.min(minY, maxY) : undefined
  const bandHeight =
    minY !== undefined && maxY !== undefined ? Math.abs(maxY - minY) : undefined

  // The selection brackets the close envelope entirely → draw the band
  // spanning the whole chart so the user still gets "your range covers this"
  // feedback even when the bounds themselves are off-screen.
  const selectionWrapsCloseEnvelope =
    minPrice !== undefined &&
    maxPrice !== undefined &&
    Number.isFinite(minPrice) &&
    Number.isFinite(maxPrice) &&
    minPrice <= minValue &&
    maxPrice >= maxValue

  // Human-readable badge for when the selection sits outside the visible
  // window. The view only knows the numeric relation; we keep copy generic.
  const rangeStatusLabel =
    minPrice === undefined || maxPrice === undefined
      ? undefined
      : selectionWrapsCloseEnvelope
        ? "Range covers full window"
        : !minInFrame && !maxInFrame
          ? "Range outside window"
          : !minInFrame
            ? "Lower bound off-chart"
            : !maxInFrame
              ? "Upper bound off-chart"
              : undefined

  const deltaPct =
    closeValues.length >= 2 && closeValues[0] !== 0
      ? ((closeValues[closeValues.length - 1]! - closeValues[0]!) / closeValues[0]!) * 100
      : undefined

  return (
    <div
      className={cn(
        "bg-p400 border-p400-light text-t100 flex w-full flex-col gap-3 rounded-md border p-3",
        className,
      )}
      data-testid="price-history-root"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="text-t200 text-xs uppercase tracking-wide">Price history</span>
          <span className="text-t300 text-[10px]">
            {quoteSymbol} / {baseSymbol}
          </span>
          {deltaPct !== undefined ? (
            <span
              className={cn(
                "text-[10px] font-medium tabular-nums",
                deltaPct >= 0 ? "text-primary-400" : "text-error-text",
              )}
              data-testid="price-history-delta"
            >
              {deltaPct >= 0 ? "+" : ""}
              {deltaPct.toFixed(2)}%
            </span>
          ) : null}
          <span
            className={cn(
              "text-[10px] font-medium uppercase tracking-wide",
              isInRange ? "text-primary-400" : "text-t300",
            )}
            data-testid="price-history-in-range"
          >
            {isInRange ? "In range" : "Out of range"}
          </span>
          {rangeStatusLabel ? (
            <span
              className="text-t300 text-[10px] uppercase tracking-wide"
              data-testid="price-history-range-status"
            >
              {rangeStatusLabel}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <div
            className="bg-p300 border-p300-light inline-flex overflow-hidden rounded border"
            data-testid="price-history-zoom-controls"
            role="group"
            aria-label="Zoom price history"
          >
            <button
              type="button"
              onClick={onZoomOut}
              disabled={!onZoomOut || !canZoomOut}
              className={cn(
                "text-t200 hover:bg-p300-light disabled:hover:bg-p300 flex items-center justify-center px-1.5 py-0.5 transition-colors",
                (!onZoomOut || !canZoomOut) && "cursor-default opacity-40",
              )}
              aria-label="Zoom out price history"
              data-testid="price-history-zoom-out"
            >
              <ZoomOut className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={onZoomIn}
              disabled={!onZoomIn || !canZoomIn}
              className={cn(
                "text-t200 hover:bg-p300-light disabled:hover:bg-p300 border-p300-light flex items-center justify-center border-l px-1.5 py-0.5 transition-colors",
                (!onZoomIn || !canZoomIn) && "cursor-default opacity-40",
              )}
              aria-label="Zoom in price history"
              data-testid="price-history-zoom-in"
            >
              <ZoomIn className="h-3 w-3" />
            </button>
          </div>
          <nav
            className="bg-p300 border-p300-light inline-flex overflow-hidden rounded border text-[10px]"
            aria-label="Chart timeframe"
            data-testid="price-history-windows"
          >
            {WINDOW_OPTIONS.map((option) => {
              const selected = option.id === window
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onWindowChange(option.id)}
                  aria-pressed={selected}
                  className={cn(
                    "px-2 py-0.5 font-medium transition-colors",
                    selected ? "bg-p200 text-t100" : "text-t200 hover:bg-p300-light",
                  )}
                  data-testid={`price-history-window-${option.id}`}
                >
                  {option.label}
                </button>
              )
            })}
          </nav>
        </div>
      </div>

      <div className="relative">
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label="Historical pool price with selected range overlay"
          className={cn("w-full select-none", isLoading && "opacity-60")}
          style={{ height }}
          data-testid="price-history-svg"
        >
          <defs>
            <linearGradient id="price-history-band-gradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary-400))" stopOpacity="0.22" />
              <stop offset="100%" stopColor="hsl(var(--primary-400))" stopOpacity="0.08" />
            </linearGradient>
          </defs>

          {bandTop !== undefined && bandHeight !== undefined ? (
            <rect
              x={0}
              y={bandTop}
              width={CHART_WIDTH}
              height={Math.max(1, bandHeight)}
              fill="url(#price-history-band-gradient)"
              data-testid="price-history-range-band"
            />
          ) : selectionWrapsCloseEnvelope ? (
            <rect
              x={0}
              y={CHART_PAD_Y}
              width={CHART_WIDTH}
              height={usableH}
              fill="url(#price-history-band-gradient)"
              data-testid="price-history-range-band"
            />
          ) : null}

          {minY !== undefined ? (
            <line
              x1={0}
              x2={CHART_WIDTH}
              y1={minY}
              y2={minY}
              stroke="hsl(var(--primary-400))"
              strokeWidth="1"
              strokeOpacity="0.7"
              strokeDasharray="4 4"
              data-testid="price-history-min-line"
            />
          ) : null}
          {maxY !== undefined ? (
            <line
              x1={0}
              x2={CHART_WIDTH}
              y1={maxY}
              y2={maxY}
              stroke="hsl(var(--primary-400))"
              strokeWidth="1"
              strokeOpacity="0.7"
              strokeDasharray="4 4"
              data-testid="price-history-max-line"
            />
          ) : null}

          {currentY !== undefined ? (
            <line
              x1={0}
              x2={CHART_WIDTH}
              y1={currentY}
              y2={currentY}
              stroke="hsl(var(--t200))"
              strokeWidth="1"
              strokeOpacity="0.5"
              strokeDasharray="2 4"
              data-testid="price-history-current-line"
            />
          ) : null}

          {pricePath ? (
            <path
              d={pricePath}
              fill="none"
              stroke="hsl(var(--t100))"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              data-testid="price-history-line"
            />
          ) : null}
        </svg>
        {!isLoading && closeValues.length === 0 ? (
          <div
            className="text-t300 absolute inset-0 flex items-center justify-center text-xs"
            data-testid="price-history-empty"
          >
            No price history yet
          </div>
        ) : null}
        {isLoading && closeValues.length === 0 ? (
          <div
            className="text-t300 absolute inset-0 flex items-center justify-center text-xs"
            data-testid="price-history-loading"
          >
            Loading…
          </div>
        ) : null}
      </div>
    </div>
  )
}
